import { useState } from 'react';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { formatIQD, formatDuration } from '../../../../shared/format.js';

function pickTotal(r) {
  return r?.total ?? r?.amount ?? r?.estimatedAmount ?? r?.data?.total ?? 0;
}

function pickBreakdown(r) {
  return r?.breakdown || r?.lines || r?.data?.breakdown || [];
}

function pickHours(r) {
  return r?.hours ?? r?.data?.hours ?? null;
}

function pickEffectiveMinutes(r) {
  return r?.effectiveMinutes ?? r?.minutes ?? r?.data?.effectiveMinutes ?? null;
}

function pickGraceApplied(r) {
  return r?.graceApplied ?? r?.data?.graceApplied ?? null;
}

function pickDiscount(r) {
  return r?.discount ?? r?.discountApplied ?? r?.data?.discount ?? null;
}

export function BreakdownDisplay({ result, loading, error }) {
  const [showJson, setShowJson] = useState(false);

  if (loading) {
    return (
      <div className="card-b text-center text-ink-3 py-10">جاري الحساب...</div>
    );
  }
  if (error) {
    return (
      <div className="card-b text-center text-red py-6">{error}</div>
    );
  }
  if (!result) {
    return (
      <div className="card-b text-center text-ink-3 py-10 flex flex-col items-center gap-2">
        <Icon name="pricing" size={36} color="#D6CCB3" />
        <div>اختر إعدادات المحاكاة واضغط "احسب"</div>
      </div>
    );
  }

  const total = pickTotal(result);
  const breakdown = pickBreakdown(result);
  const hours = pickHours(result);
  const effMin = pickEffectiveMinutes(result);
  const grace = pickGraceApplied(result);
  const discount = pickDiscount(result);

  return (
    <div className="card-b space-y-3">
      <div className="p-4 rounded-[12px] bg-orange-soft text-orange-deep flex items-center justify-between">
        <div>
          <div className="text-[12px] font-semibold">الإجمالي المتوقع</div>
          <div className="text-[11px] opacity-80 mt-0.5">حسب القاعدة الحالية</div>
        </div>
        <div className="text-[28px] font-extrabold">{formatIQD(total)}</div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-[12.5px]">
        <div className="p-2.5 rounded-[10px] bg-paper-2">
          <div className="text-ink-3 text-[11px]">الساعات</div>
          <div className="font-bold mt-0.5">{hours != null ? hours : '—'}</div>
        </div>
        <div className="p-2.5 rounded-[10px] bg-paper-2">
          <div className="text-ink-3 text-[11px]">دقائق فعّالة</div>
          <div className="font-bold mt-0.5">{effMin != null ? formatDuration(effMin) : '—'}</div>
        </div>
        <div className="p-2.5 rounded-[10px] bg-paper-2">
          <div className="text-ink-3 text-[11px]">سماح مطبّق</div>
          <div className="font-bold mt-0.5">
            {grace == null ? '—' : grace ? <Pill variant="success">نعم</Pill> : <Pill variant="muted">لا</Pill>}
          </div>
        </div>
      </div>

      {Array.isArray(breakdown) && breakdown.length ? (
        <div>
          <div className="text-xs font-semibold text-ink-2 mb-1.5">تفصيل الفاتورة</div>
          <table className="w-full text-[12.5px]">
            <thead className="text-ink-3 text-[11px]">
              <tr className="border-b border-line">
                <th className="p-2 text-start">البند</th>
                <th className="p-2 text-start">النوع</th>
                <th className="p-2 text-end">المبلغ</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((b, i) => (
                <tr key={i} className="border-b border-line">
                  <td className="p-2">{b.label || b.name || b.title || '—'}</td>
                  <td className="p-2 text-ink-3">{b.type || b.kind || '—'}</td>
                  <td
                    className="p-2 text-end font-bold"
                    style={{ color: (b.amount || 0) < 0 ? '#15803D' : '#1A1A1A' }}
                  >
                    {formatIQD(b.amount || b.value || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {discount ? (
        <div className="p-2.5 rounded-[10px] bg-[#DCFCE7] text-[#15803D] text-[12.5px]">
          خصم مطبّق: {typeof discount === 'object' ? (discount.label || discount.name || JSON.stringify(discount)) : String(discount)}
        </div>
      ) : null}

      <button
        type="button"
        className="btn btn-ghost text-[12px]"
        onClick={() => setShowJson((v) => !v)}
      >
        <Icon name={showJson ? 'chevronD' : 'chevronL'} size={12} />
        {showJson ? 'إخفاء JSON' : 'عرض استجابة الخادم (JSON)'}
      </button>
      {showJson ? (
        <pre className="text-[11px] bg-paper-2 p-3 rounded-[10px] overflow-auto max-h-[300px]" dir="ltr">
{JSON.stringify(result, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}
